import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  DialogContentText,
  Typography,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import SelectRol from "../../components/SelectRol-Usuario/SelectRol";
import CloseIcon from '@mui/icons-material/Close';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";

interface ActualizarUsuarioProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: any) => void;
  id?: number;
}

const validationSchema = Yup.object({
  nombre: Yup.string().required("Nombre es requerido"),
  usuario: Yup.string().required("Usuario es requerido"),
  correo: Yup.string().email("Correo invalido").required("Correo es requerido"),
  contraseña: Yup.string().min(6, "Minimo 6 caracteres").optional(),
  rol: Yup.string().required("Rol es requerido"),
});

const ActualizarUsuario: React.FC<ActualizarUsuarioProps> = ({
  open,
  onClose,
  onSubmit,
  id,
}) => {
  const [initialValues, setInitialValues] = useState({
    nombre: "",
    usuario: "",
    correo: "",
    contraseña: "",
    rol: "",
  });
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (id !== undefined) {
      const fetchData = async () => {
        try {
          const storedToken = localStorage.getItem("authToken");
          const token = storedToken ? atob(storedToken) : "";

          const response = await fetch(
            `${getProyectosApiBaseUrl()}/usuarios/${id}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error("Failed to fetch data");
          }

          const data = await response.json();
          setInitialValues({
            nombre: data.nombre,
            usuario: data.usuario,
            correo: data.correo || "",
            contraseña: "",
            rol: data.rol.nombre,
          });
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      };

      fetchData();
    }
  }, [id]);

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      const storedToken = localStorage.getItem("authToken");
      const token = storedToken ? atob(storedToken) : "";
      const storedUser = localStorage.getItem("user");
      const User = storedUser ? atob(storedUser) : "";

      const body: Record<string, unknown> = {
        nombre: values.nombre,
        usuario: values.usuario,
        correo: String(values.correo || "").trim().toLowerCase(),
        rol: values.rol,
        usuarioCreador: User,
      };
      if (values.contraseña.trim()) {
        body.contraseña = values.contraseña.trim();
      }

      const response = await fetch(
        `${getProyectosApiBaseUrl()}/usuarios/${id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || "No fue posible actualizar el usuario.");
      }

      const data = await response.json();
      setSuccessMessage("Usuario actualizado exitosamente");
      setSuccessDialogOpen(true);
      onSubmit(data);
      onClose();
    } catch (error: any) {
      console.error("Error:", error);
      alert(error?.message || "Error al actualizar.");
    }
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialogOpen(false);
    setTimeout(() => {
      window.location.reload();
    }, 10000);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Actualizar Usuario</DialogTitle>
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(false);
            handleSubmit(values);
          }}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form>
              <DialogContent>
                <Field
                  as={TextField}
                  name="nombre"
                  label="Nombre"
                  fullWidth
                  error={touched.nombre && !!errors.nombre}
                  helperText={touched.nombre && errors.nombre}
                  margin="normal"
                />
                <Field
                  as={TextField}
                  name="usuario"
                  label="Usuario"
                  fullWidth
                  error={touched.usuario && !!errors.usuario}
                  helperText={touched.usuario && errors.usuario}
                  margin="normal"
                />
                <Field
                  as={TextField}
                  name="correo"
                  label="Correo electronico"
                  type="email"
                  fullWidth
                  error={touched.correo && !!errors.correo}
                  helperText={touched.correo && errors.correo}
                  margin="normal"
                />
                <Field
                  as={TextField}
                  name="contraseña"
                  label="Nueva contraseña (opcional)"
                  type="password"
                  fullWidth
                  error={touched.contraseña && !!errors.contraseña}
                  helperText={
                    (touched.contraseña && errors.contraseña) ||
                    "Deje en blanco para mantener la contraseña actual."
                  }
                  margin="normal"
                />
                <SelectRol name="rol" onlyProjectRoles />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Si cambia el rol se reaplican los permisos base de ese rol. Permisos adicionales en «Permisos gestion proyectos».
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose} color="error" variant="contained" endIcon={<CloseIcon />}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  endIcon={<DoneOutlineIcon />}
                  disabled={isSubmitting}
                >
                  Guardar
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      <Dialog open={successDialogOpen} onClose={handleCloseSuccessDialog}>
        <DialogTitle>Éxito</DialogTitle>
        <DialogContent>
          <DialogContentText>{successMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSuccessDialog} color="primary" variant="contained">
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ActualizarUsuario;
